<template>
	<div v-if="visible" class="favorite-panel-mask" @click.self="handleClose">
		<div class="favorite-panel">
			<div class="favorite-header">
				<div class="favorite-title">收藏</div>
				<i class="favorite-close el-icon-close" @click="handleClose"></i>
			</div>
			<div class="favorite-search" :class="{ active: isSearchActive }">
				<div v-if="!isSearchActive" class="favorite-search-placeholder">
					<i class="el-icon-search"></i>
					<span>搜索</span>
				</div>
				<el-input size="small" :placeholder="isSearchActive ? '搜索' : ''" v-model="searchText" clearable
					@focus="searchFocused = true" @blur="searchFocused = false">
					<i v-if="isSearchActive" class="el-icon-search el-input__icon" slot="prefix"></i>
				</el-input>
			</div>
			<div class="favorite-tabs">
				<span v-for="tab in tabs" :key="tab.key" class="favorite-tab"
					:class="{ active: activeTab === tab.key }" @click="activeTab = tab.key">
					{{ tab.name }}
				</span>
			</div>
			<el-scrollbar class="favorite-list">
				<div v-if="loading" class="favorite-empty">加载中...</div>
				<div v-else-if="filteredFavorites.length === 0" class="favorite-empty">暂无收藏</div>
				<div v-else v-for="item in filteredFavorites" :key="item.id" class="favorite-card"
					:class="{ selected: isFavoriteSelected(item), 'selection-mode': selectionMode, 'picker-mode': mode === 'picker' }"
					@click="onFavoriteCardClick(item)"
					@contextmenu.prevent="openFavoriteMenu($event, item)"
					@mousedown="startFavoriteLongPress($event, item)"
					@mouseup="clearFavoriteLongPress"
					@mouseleave="clearFavoriteLongPress"
					@touchstart="startFavoriteLongPress($event, item)"
					@touchend="clearFavoriteLongPress"
					@touchcancel="clearFavoriteLongPress">
					<div v-if="selectionMode" class="favorite-select-toggle"
						:class="{ checked: isFavoriteSelected(item) }"
						@click.stop="toggleFavoriteSelection(item)"></div>
					<div class="favorite-card-main">
						<div class="favorite-content">
							<div class="favorite-card-title">{{ getFavoriteTitle(item) }}</div>
							<div v-if="isMergedForwardFavorite(item)" class="favorite-merged-lines">
								<div v-for="(line, index) in getMergedForwardSummaryLines(item)" :key="index"
									class="favorite-merged-line">
									{{ line }}
								</div>
							</div>
							<div v-else-if="isLinkFavorite(item)" class="favorite-summary favorite-link-summary"
								@click.stop="openFavoriteLink(item)">
								{{ getFavoriteLinkText(item) }}
							</div>
							<div v-else-if="!isImageFavorite(item)" class="favorite-summary">
								{{ getFavoriteSummary(item) }}
							</div>
							<div v-if="isFileFavorite(item)" class="favorite-file-size">{{ getFileSize(item) }}</div>
						</div>
						<img v-if="isImageFavorite(item) && getImagePreview(item)" class="favorite-image-preview"
							:src="getImagePreview(item)" alt="图片" />
						<div v-else-if="isFileFavorite(item)" class="favorite-file-thumb">
							<span class="favorite-file-paper">
								<span class="favorite-file-fold"></span>
								<span class="favorite-file-ext">{{ getFileExt(item) }}</span>
							</span>
						</div>
					</div>
					<div class="favorite-footer" :class="{ 'picker-mode': mode === 'picker' }">
						<span class="favorite-source">{{ getFavoriteSource(item) }}</span>
						<span class="favorite-time">{{ formatTime(item.createdTime) }}</span>
					</div>
				</div>
			</el-scrollbar>
			<div v-if="selectionMode" class="favorite-multi-select-bar" @click.stop>
				<span class="favorite-multi-select-count">已选 {{ selectedFavoriteIds.length }} 条</span>
				<el-button size="mini" type="primary" @click="batchForwardFavorites">转发</el-button>
				<el-button size="mini" type="danger" @click="batchDeleteFavorites">删除</el-button>
				<el-button size="mini" @click="cancelFavoriteMultiSelect">取消</el-button>
			</div>
		</div>
		<right-menu ref="favoriteMenu" @select="onFavoriteMenuSelect"></right-menu>
		<forward-select :visible="showForwardSelect" :forwardMsg="forwardMsg" :forwardBatch="forwardBatch"
			@close="closeForwardSelect" @batchForwarded="onFavoriteBatchForwarded"></forward-select>
		<div v-if="showMergedForwardDetail" class="merged-forward-detail-mask" @click.stop="closeMergedForwardDetail">
			<div class="merged-forward-detail-panel" @click.stop>
				<div class="merged-forward-detail-header">
					<span class="merged-forward-detail-title">{{ currentMergedForwardData && currentMergedForwardData.title }}</span>
					<i class="merged-forward-detail-close el-icon-close" @click="closeMergedForwardDetail"></i>
				</div>
				<div class="merged-forward-detail-body">
					<div v-for="(record, index) in favoriteMergedForwardRecords" :key="index" class="merged-forward-record">
						<div class="merged-forward-record-meta">
							<span class="merged-forward-record-name">{{ record.sendNickName || '未知用户' }}</span>
							<span v-if="record.sendTime" class="merged-forward-record-time">{{ formatMergedForwardRecordTime(record.sendTime) }}</span>
						</div>
						<img v-if="isMergedForwardImage(record)" class="merged-forward-record-image"
							:src="getMergedForwardImageSrc(record)" alt="图片" />
						<div v-else-if="isMergedForwardFile(record)" class="merged-forward-record-file"
							@click.stop="openMergedForwardFile(record)">
							<div class="merged-forward-record-file-info">
								<div class="merged-forward-record-file-name">{{ getMergedForwardFileName(record) }}</div>
								<div class="merged-forward-record-file-size">{{ getMergedForwardFileSize(record) }}</div>
							</div>
							<div class="merged-forward-record-file-icon" :class="getMergedForwardFileIconMeta(record).className">
								<div class="file-icon-paper">
									<div class="file-icon-fold"></div>
									<span class="file-icon-mark">{{ getMergedForwardFileIconMeta(record).mark }}</span>
								</div>
							</div>
						</div>
						<div v-else-if="isMergedForwardRecord(record)" class="merged-forward-record-card"
							@click="openNestedMergedForwardDetail(record)">
							<div class="merged-forward-record-card-title">{{ getNestedMergedForwardData(record).title }}</div>
							<div class="merged-forward-record-card-lines">
								<div v-for="(line, lineIndex) in getNestedMergedForwardData(record).summaryLines" :key="lineIndex"
									class="merged-forward-record-card-line">
									{{ line }}
								</div>
							</div>
							<div class="merged-forward-record-card-divider"></div>
							<div class="merged-forward-record-card-tip">聊天记录</div>
						</div>
						<div v-else class="merged-forward-record-bubble">{{ record.text || getMergedForwardRecordText(record) }}</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { getFileIconMetaFromContent } from "../../utils/fileIcon";
import ForwardSelect from "../friend/ForwardSelect.vue";
import RightMenu from "../common/RightMenu.vue";

export default {
	name: 'FavoritePanel',
	components: {
		ForwardSelect,
		RightMenu
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		mode: {
			type: String,
			default: 'list'
		}
	},
	data() {
		return {
			loading: false,
			searchText: '',
			searchFocused: false,
			activeTab: 'recent',
			showMergedForwardDetail: false,
			mergedForwardDetailStack: [],
			favoriteMergedForwardData: null,
			selectionMode: false,
			selectedFavoriteIds: [],
			activeFavorite: null,
			favoriteLongPressTimer: null,
			favoriteLongPressTriggered: false,
			showForwardSelect: false,
			forwardMsg: {},
			forwardBatch: null,
			tabs: [
				{ key: 'recent', name: '最近使用' },
				{ key: 'chat', name: '聊天记录' },
				{ key: 'media', name: '文件-图片-视频' },
				{ key: 'link', name: '链接' },
				{ key: 'location', name: '位置' }
			],
			favorites: []
		}
	},
	computed: {
		isSearchActive() {
			return this.searchFocused || !!this.searchText;
		},
		filteredFavorites() {
			const keyword = (this.searchText || '').trim().toLowerCase();
			return this.favorites.filter((item) => {
				if (!this.matchesFavoriteTab(item)) {
					return false;
				}
				if (!keyword) {
					return true;
				}
				return [
					this.getFavoriteTitle(item),
					this.getFavoriteSummary(item),
					item.sendNickName,
					this.getMergedForwardSummaryLines(item).join(' ')
				].join(' ').toLowerCase().indexOf(keyword) >= 0;
			});
		},
		currentMergedForwardData() {
			if (this.mergedForwardDetailStack.length) {
				return this.mergedForwardDetailStack[this.mergedForwardDetailStack.length - 1];
			}
			return this.favoriteMergedForwardData;
		},
		favoriteMergedForwardRecords() {
			const data = this.currentMergedForwardData;
			return data && Array.isArray(data.records) ? data.records : [];
		}
	},
	watch: {
		visible(val) {
			if (val) {
				this.loadFavorites();
			}
		}
	},
	beforeDestroy() {
		this.clearFavoriteLongPress();
	},
	methods: {
		handleClose() {
			this.cancelFavoriteMultiSelect();
			this.closeForwardSelect();
			this.$emit('close');
		},
		loadFavorites() {
			this.loading = true;
			this.$http({
				url: '/favorite/list',
				method: 'GET',
				params: {
					page: 1,
					size: 100
				}
			}).then((res) => {
				this.favorites = Array.isArray(res) ? res : [];
				this.syncSelectedFavorites();
			}).finally(() => {
				this.loading = false;
			});
		},
		favoriteMenuItems() {
			return [
				{ key: 'FORWARD', name: '转发' },
				{ key: 'DELETE', name: '删除' },
				{ key: 'MULTI_SELECT', name: '多选' }
			];
		},
		getEventPosition(event) {
			if (!event) {
				return { clientX: 0, clientY: 0 };
			}
			const touch = event.touches && event.touches[0]
				? event.touches[0]
				: (event.changedTouches && event.changedTouches[0] ? event.changedTouches[0] : null);
			return touch || event;
		},
		openFavoriteMenu(event, item) {
			if (this.mode === 'picker' || this.selectionMode) {
				return;
			}
			this.activeFavorite = item;
			this.$refs.favoriteMenu && this.$refs.favoriteMenu.open(this.getEventPosition(event), this.favoriteMenuItems());
		},
		startFavoriteLongPress(event, item) {
			if (this.mode === 'picker' || this.selectionMode) {
				return;
			}
			if (event && event.type === 'mousedown' && event.button !== 0) {
				return;
			}
			this.clearFavoriteLongPress();
			this.favoriteLongPressTriggered = false;
			this.favoriteLongPressTimer = setTimeout(() => {
				this.favoriteLongPressTriggered = true;
				this.openFavoriteMenu(event, item);
			}, 600);
		},
		clearFavoriteLongPress() {
			if (this.favoriteLongPressTimer) {
				clearTimeout(this.favoriteLongPressTimer);
				this.favoriteLongPressTimer = null;
			}
		},
		onFavoriteMenuSelect(menuItem) {
			const item = this.activeFavorite;
			this.activeFavorite = null;
			if (!item || !menuItem) {
				return;
			}
			if (menuItem.key === 'FORWARD') {
				this.onForwardFavorite(item);
				return;
			}
			if (menuItem.key === 'DELETE') {
				this.onDeleteFavorite(item);
				return;
			}
			if (menuItem.key === 'MULTI_SELECT') {
				this.enterFavoriteMultiSelect(item);
			}
		},
		onFavoriteCardClick(item) {
			if (this.favoriteLongPressTriggered) {
				this.favoriteLongPressTriggered = false;
				return;
			}
			if (this.selectionMode) {
				this.toggleFavoriteSelection(item);
				return;
			}
			if (this.mode === 'picker') {
				this.confirmSendFavorite(item);
				return;
			}
			this.previewFavorite(item);
		},
		confirmSendFavorite(item) {
			this.$confirm('确认发送该收藏?', '发送收藏', {
				confirmButtonText: '发送',
				cancelButtonText: '取消',
				type: 'warning',
				closeOnClickModal: false
			}).then(() => {
				this.$emit('send', item);
			}).catch(() => { });
		},
		getFavoriteSelectId(item) {
			return item && item.id ? item.id : null;
		},
		isFavoriteSelected(item) {
			const id = this.getFavoriteSelectId(item);
			return !!id && this.selectedFavoriteIds.indexOf(id) >= 0;
		},
		toggleFavoriteSelection(item) {
			const id = this.getFavoriteSelectId(item);
			if (!id) {
				return;
			}
			const index = this.selectedFavoriteIds.indexOf(id);
			if (index >= 0) {
				this.selectedFavoriteIds.splice(index, 1);
				return;
			}
			this.selectedFavoriteIds.push(id);
		},
		enterFavoriteMultiSelect(item) {
			this.selectionMode = true;
			this.selectedFavoriteIds = [];
			this.toggleFavoriteSelection(item);
		},
		cancelFavoriteMultiSelect() {
			this.selectionMode = false;
			this.selectedFavoriteIds = [];
		},
		syncSelectedFavorites() {
			if (!this.selectedFavoriteIds.length) {
				return;
			}
			const ids = new Set(this.favorites.map(item => item.id));
			this.selectedFavoriteIds = this.selectedFavoriteIds.filter(id => ids.has(id));
			if (!this.selectedFavoriteIds.length) {
				this.selectionMode = false;
			}
		},
		getSelectedFavorites() {
			const ids = new Set(this.selectedFavoriteIds);
			return this.favorites.filter(item => ids.has(item.id));
		},
		getFavoriteForwardContent(item) {
			if (!item) {
				return undefined;
			}
			if (item.content !== undefined && item.content !== null) {
				return item.content;
			}
			return item.contentSnapshot && item.contentSnapshot.content;
		},
		getFavoriteForwardType(item) {
			if (!item) {
				return undefined;
			}
			return item.messageType !== undefined && item.messageType !== null ? item.messageType : item.type;
		},
		buildFavoriteForwardMessage(item) {
			const content = this.getFavoriteForwardContent(item);
			const type = this.getFavoriteForwardType(item);
			if ((content === undefined || content === null) || type === undefined || type === null) {
				this.$message.warning('收藏内容为空');
				return null;
			}
			return {
				content,
				type,
				sendTime: item.sendTime || item.createdTime,
				sendNickName: item.sendNickName || this.getFavoriteSource(item),
				text: this.getFavoriteSummary(item) || this.getFavoriteTitle(item)
			};
		},
		onForwardFavorite(item) {
			const msg = this.buildFavoriteForwardMessage(item);
			if (!msg) {
				return;
			}
			this.forwardMsg = msg;
			this.forwardBatch = null;
			this.showForwardSelect = true;
		},
		batchForwardFavorites() {
			const messages = this.getSelectedFavorites()
				.map(item => this.buildFavoriteForwardMessage(item))
				.filter(Boolean);
			if (!messages.length) {
				this.$message.warning('请选择可转发的收藏');
				return;
			}
			if (messages.length === 1) {
				this.forwardMsg = messages[0];
				this.forwardBatch = null;
			} else {
				this.forwardMsg = {};
				this.forwardBatch = {
					mode: 'separate',
					messages
				};
			}
			this.cancelFavoriteMultiSelect();
			this.showForwardSelect = true;
		},
		closeForwardSelect() {
			this.showForwardSelect = false;
			this.forwardMsg = {};
			this.forwardBatch = null;
		},
		onFavoriteBatchForwarded() {
			this.cancelFavoriteMultiSelect();
		},
		deleteFavoriteRequest(item) {
			return this.$http({
				url: `/favorite/${item.id}`,
				method: 'DELETE'
			});
		},
		removeFavorites(items) {
			const ids = new Set(items.map(item => item.id));
			this.favorites = this.favorites.filter(favorite => !ids.has(favorite.id));
			this.selectedFavoriteIds = this.selectedFavoriteIds.filter(id => !ids.has(id));
			if (!this.selectedFavoriteIds.length) {
				this.selectionMode = false;
			}
		},
		onDeleteFavorite(item) {
			if (!item || !item.id) {
				return;
			}
			this.$confirm('确认删除该收藏?', '删除收藏', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.deleteFavoriteRequest(item).then(() => {
					this.removeFavorites([item]);
					this.$message.success('已删除');
				});
			});
		},
		batchDeleteFavorites() {
			const favorites = this.getSelectedFavorites();
			if (!favorites.length) {
				this.$message.warning('请选择收藏');
				return;
			}
			this.$confirm(`确认删除选中的${favorites.length}条收藏?`, '删除收藏', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				Promise.all(favorites.map(item => this.deleteFavoriteRequest(item))).then(() => {
					this.removeFavorites(favorites);
					this.$message.success('已删除');
				});
			});
		},
		getFavoriteRawContent(item) {
			if (!item) {
				return '';
			}
			if (item.content !== undefined && item.content !== null) {
				return item.content;
			}
			return item.contentSnapshot && item.contentSnapshot.content;
		},
		parseContent(item) {
			const content = this.getFavoriteRawContent(item);
			if (!content) {
				return {};
			}
			if (typeof content === 'object') {
				return content;
			}
			try {
				return JSON.parse(content);
			} catch (e) {
				return {};
			}
		},
		matchesFavoriteTab(item) {
			if (this.activeTab === 'media') {
				return this.isFileFavorite(item) || this.isImageFavorite(item) || this.isVideoFavorite(item);
			}
			if (this.activeTab === 'link') {
				return this.isLinkFavorite(item);
			}
			if (this.activeTab === 'chat') {
				return item && item.messageType === this.$enums.MESSAGE_TYPE.MERGED_FORWARD;
			}
			if (this.activeTab === 'location') {
				return this.isLocationFavorite(item);
			}
			return true;
		},
		getFavoriteLinkMatch(item) {
			const text = this.getFavoriteText(item);
			return String(text || '').match(/(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|]|\bwww\.[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/i);
		},
		isLinkFavorite(item) {
			return item && item.messageType === this.$enums.MESSAGE_TYPE.TEXT && !!this.getFavoriteLinkUrl(item);
		},
		getFavoriteLinkText(item) {
			const match = this.getFavoriteLinkMatch(item);
			return match ? match[0] : this.getFavoriteSummary(item);
		},
		getFavoriteLinkUrl(item) {
			const match = this.getFavoriteLinkMatch(item);
			if (!match) {
				return '';
			}
			let url = match[0];
			if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
				url = (process.env.VUE_APP_DEFAULT_URL_PROTOCOL || 'http://') + url;
			}
			return url;
		},
		openFavoriteLink(item) {
			const url = this.getFavoriteLinkUrl(item);
			if (!url) {
				this.$message.error('链接地址无效');
				return;
			}
			window.open(url, '_blank', 'noopener,noreferrer');
		},
		isImageFavorite(item) {
			return item && item.messageType === this.$enums.MESSAGE_TYPE.IMAGE;
		},
		isVideoFavorite(item) {
			return item && item.messageType === this.$enums.MESSAGE_TYPE.VIDEO;
		},
		isFileFavorite(item) {
			return item && item.messageType === this.$enums.MESSAGE_TYPE.FILE;
		},
		isLocationFavorite(item) {
			return item && item.messageType === this.$enums.MESSAGE_TYPE.LOCATION;
		},
		isMergedForwardFavorite(item) {
			return item && item.messageType === this.$enums.MESSAGE_TYPE.MERGED_FORWARD;
		},
		getImagePreview(item) {
			const data = this.parseContent(item);
			return data.thumbUrl || data.originUrl || data.url || '';
		},
		getFavoriteTitle(item) {
			if (!item) return '收藏';
			if (this.isFileFavorite(item)) {
				return this.getFileName(item);
			}
			if (this.isImageFavorite(item)) {
				return item.title || item.sendNickName || '图片';
			}
			if (this.isMergedForwardFavorite(item)) {
				const data = this.getMergedForwardData(item);
				return data.title || item.title || '聊天记录';
			}
			return item.title || item.sendNickName || this.getContentSummary(item) || '收藏';
		},
		getFavoriteSource(item) {
			return item && item.sendNickName ? item.sendNickName : (item && item.sourceType === 'GROUP' ? '群聊' : '单聊');
		},
		getFileName(item) {
			const data = this.parseContent(item);
			return data.name || data.fileName || item.title || '[文件]';
		},
		getFileSize(item) {
			const data = this.parseContent(item);
			if (!data.size && !data.fileSize) {
				return '';
			}
			const size = Number(data.size || data.fileSize);
			if (!Number.isFinite(size)) {
				return data.size || data.fileSize;
			}
			if (size > 1024 * 1024) {
				return Math.round(size / 1024 / 1024) + 'M';
			}
			if (size > 1024) {
				return Math.round(size / 1024) + 'KB';
			}
			return size + 'B';
		},
		getFileExt(item) {
			const data = this.parseContent(item);
			const name = data.name || data.fileName || data.url || data.originUrl || '';
			const ext = String(name).split('.').pop();
			if (!ext || ext === name) {
				return 'FILE';
			}
			return ext.slice(0, 4).toUpperCase();
		},
		getFavoriteFileUrl(item) {
			const data = this.parseContent(item);
			return data.url || data.originUrl || '';
		},
		openFavoriteFile(item) {
			const url = this.getFavoriteFileUrl(item);
			if (!url) {
				this.$message.error('文件地址无效');
				return;
			}
			window.open(url, '_blank', 'noopener,noreferrer');
		},
		cleanFavoriteText(text) {
			if (text === undefined || text === null) {
				return '';
			}
			return String(text)
				.replace(/<br\s*\/?>/gi, ' ')
				.replace(/\r?\n/g, ' ')
				.replace(/\s+/g, ' ')
				.trim();
			},
			getFavoriteText(item) {
				return this.cleanFavoriteText(this.getFavoriteRawContent(item));
			},
		getFavoriteSummary(item) {
			if (!item) return '';
			return this.cleanFavoriteText(item.summary) || this.getContentSummary(item);
		},
		getContentSummary(item) {
			if (!item) return '';
			if (item.messageType === this.$enums.MESSAGE_TYPE.TEXT) {
				return this.getFavoriteText(item);
			}
			if (this.isFileFavorite(item)) {
				return this.getFileName(item);
			}
			if (this.isImageFavorite(item)) {
				return '[图片]';
			}
			if (item.messageType === this.$enums.MESSAGE_TYPE.AUDIO) {
				return '[语音]';
			}
			if (item.messageType === this.$enums.MESSAGE_TYPE.VIDEO) {
				return '[视频]';
			}
			if (this.isLocationFavorite(item)) {
				const data = this.parseContent(item);
				return data.name || data.address || '[位置]';
			}
			if (item.messageType === this.$enums.MESSAGE_TYPE.FRIEND_CARD) {
				return '[名片]';
			}
			if (this.isMergedForwardFavorite(item)) {
				return this.getMergedForwardSummaryLines(item).join('\n') || '[聊天记录]';
			}
			return '[消息]';
		},
		getMergedForwardData(item) {
			const data = this.parseContent(item);
			return {
				title: data.title || item.title || '聊天记录',
				summaryLines: Array.isArray(data.summaryLines) ? data.summaryLines : [],
				records: Array.isArray(data.records) ? data.records : []
			};
		},
		getMergedForwardSummaryLines(item) {
			if (!this.isMergedForwardFavorite(item)) {
				return [];
			}
			const data = this.getMergedForwardData(item);
			if (data.summaryLines.length) {
				return data.summaryLines;
			}
			return data.records.map(record => `${record.sendNickName || '未知用户'}: ${this.cleanFavoriteText(record.text) || this.getMergedForwardRecordText(record)}`);
		},
		openFavoriteMergedForward(item) {
			if (!this.isMergedForwardFavorite(item)) {
				return;
			}
			this.favoriteMergedForwardData = this.getMergedForwardData(item);
			this.mergedForwardDetailStack = [];
			this.showMergedForwardDetail = true;
		},
		closeMergedForwardDetail() {
			if (this.mergedForwardDetailStack.length) {
				this.mergedForwardDetailStack.pop();
				return;
			}
			this.showMergedForwardDetail = false;
			this.favoriteMergedForwardData = null;
		},
		parseMergedForwardRecordContent(record) {
			if (!record || !record.content) {
				return {};
			}
			if (typeof record.content === 'object') {
				return record.content;
			}
			try {
				return JSON.parse(record.content);
			} catch (e) {
				return {};
			}
		},
		getMergedForwardImageSrc(record) {
			if (!record || record.type != this.$enums.MESSAGE_TYPE.IMAGE) {
				return '';
			}
			const data = this.parseMergedForwardRecordContent(record);
			return data.originUrl || data.url || data.thumbUrl || '';
		},
		isMergedForwardImage(record) {
			return record && record.type == this.$enums.MESSAGE_TYPE.IMAGE && !!this.getMergedForwardImageSrc(record);
		},
		isMergedForwardFile(record) {
			return record && record.type == this.$enums.MESSAGE_TYPE.FILE;
		},
		getMergedForwardFileData(record) {
			return this.parseMergedForwardRecordContent(record);
		},
		getMergedForwardFileName(record) {
			const data = this.getMergedForwardFileData(record);
			return data.name || data.fileName || '文件';
		},
		getMergedForwardFileSize(record) {
			const data = this.getMergedForwardFileData(record);
			const size = Number(data.size || data.fileSize || 0);
			if (!size) {
				return '';
			}
			if (size > 1024 * 1024) {
				return Math.round(size / 1024 / 1024 * 10) / 10 + 'M';
			}
			if (size > 1024) {
				return Math.round(size / 1024 * 10) / 10 + 'K';
			}
			return size + 'B';
		},
		getMergedForwardFileIconMeta(record) {
			return getFileIconMetaFromContent(record && record.content);
		},
		openMergedForwardFile(record) {
			const data = this.getMergedForwardFileData(record);
			const url = data.url || data.originUrl || '';
			if (!url) {
				this.$message.error('文件地址无效');
				return;
			}
			window.open(url, '_blank', 'noopener,noreferrer');
		},
		getNestedMergedForwardData(record) {
			const data = this.parseMergedForwardRecordContent(record);
			return {
				title: data.title || '聊天记录',
				summaryLines: Array.isArray(data.summaryLines) ? data.summaryLines : [],
				records: Array.isArray(data.records) ? data.records : []
			};
		},
		isMergedForwardRecord(record) {
			return record && record.type == this.$enums.MESSAGE_TYPE.MERGED_FORWARD && this.getNestedMergedForwardData(record).records.length > 0;
		},
		openNestedMergedForwardDetail(record) {
			if (!this.isMergedForwardRecord(record)) {
				return;
			}
			this.mergedForwardDetailStack.push(this.getNestedMergedForwardData(record));
		},
		padMergedForwardTimePart(value) {
			return String(value).padStart(2, '0');
		},
		formatMergedForwardRecordTime(sendTime) {
			const date = new Date(sendTime);
			if (Number.isNaN(date.getTime())) {
				return '';
			}
			const month = this.padMergedForwardTimePart(date.getMonth() + 1);
			const day = this.padMergedForwardTimePart(date.getDate());
			const hour = this.padMergedForwardTimePart(date.getHours());
			const minute = this.padMergedForwardTimePart(date.getMinutes());
			return `${date.getFullYear()}-${month}-${day} ${hour}:${minute}`;
		},
		getMergedForwardRecordText(record) {
			if (!record) return '[消息]';
			if (record.type == this.$enums.MESSAGE_TYPE.TEXT) {
				return this.cleanFavoriteText(record.content) || '[文本]';
			}
			if (record.type == this.$enums.MESSAGE_TYPE.IMAGE) {
				return '[图片]';
			}
			if (record.type == this.$enums.MESSAGE_TYPE.FILE) {
				return `[文件] ${this.getMergedForwardFileName(record)}`;
			}
			if (record.type == this.$enums.MESSAGE_TYPE.AUDIO) {
				return '[语音]';
			}
			if (record.type == this.$enums.MESSAGE_TYPE.VIDEO) {
				return '[视频]';
			}
			if (record.type == this.$enums.MESSAGE_TYPE.LOCATION) {
				return '[位置]';
			}
			if (record.type == this.$enums.MESSAGE_TYPE.FRIEND_CARD) {
				return '[名片]';
			}
			return '[消息]';
		},
		previewFavorite(item) {
			if (this.mode === 'picker') {
				return;
			}
				if (this.isImageFavorite(item) && this.getImagePreview(item)) {
					this.$eventBus.$emit('openFullImage', this.getImagePreview(item));
					return;
				}
				if (this.isLinkFavorite(item)) {
					this.openFavoriteLink(item);
					return;
				}
				if (this.isFileFavorite(item) || item.messageType === this.$enums.MESSAGE_TYPE.VIDEO) {
					this.openFavoriteFile(item);
					return;
				}
			if (this.isMergedForwardFavorite(item)) {
				this.openFavoriteMergedForward(item);
			}
		},
		formatFavoriteTimePart(value) {
			return String(value).padStart(2, '0');
		},
		formatTime(time) {
			if (!time) return '';
			const date = new Date(time);
			if (Number.isNaN(date.getTime())) {
				return '';
			}
			const minute = this.formatFavoriteTimePart(date.getMinutes());
			const second = this.formatFavoriteTimePart(date.getSeconds());
			return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours()}点${minute}分${second}秒`;
		}
	}
}
</script>

<style scoped lang="scss">
.favorite-panel-mask {
	position: absolute;
	inset: 0;
	/* Keep Element UI message boxes visible above delete confirmations. */
	z-index: 1900;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(17, 24, 39, 0.24);
}

.favorite-panel {
	width: 520px;
	max-width: calc(100vw - 48px);
	height: 680px;
	max-height: calc(100vh - 80px);
	background: #f2f2f5;
	border-radius: 10px;
	box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
	overflow: hidden;
	display: flex;
	flex-direction: column;
}

.favorite-header {
	height: 52px;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border-bottom: 1px solid #eeeeee;
}

.favorite-title {
	font-size: 18px;
	color: #1f2329;
	font-weight: 500;
}

.favorite-close {
	position: absolute;
	right: 18px;
	top: 17px;
	cursor: pointer;
	color: #8a8f99;
}

.favorite-search {
	position: relative;
	padding: 16px 20px 10px;
}

.favorite-search-placeholder {
	position: absolute;
	left: 20px;
	right: 20px;
	top: 16px;
	height: 32px;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	color: #9fa0a8;
	font-size: 14px;
	pointer-events: none;
}

.favorite-search-placeholder .el-icon-search {
	font-size: 16px;
	color: #b4b4bc;
}

.favorite-tabs {
	display: flex;
	justify-content: space-between;
	gap: 0;
	padding: 0 20px 14px;
	color: #596883;
	font-size: 14px;
}

.favorite-tab {
	flex: 0 0 auto;
	cursor: pointer;
}

.favorite-tab.active {
	color: #425a84;
	font-weight: 600;
}

.favorite-list {
	flex: 1;
	padding: 0 20px 18px;
	box-sizing: border-box;
}

.favorite-empty {
	padding: 80px 0;
	text-align: center;
	color: #9ca3af;
}

.favorite-card {
	position: relative;
	background: #ffffff;
	border-radius: 10px;
	padding: 18px 20px;
	margin-bottom: 14px;
	cursor: pointer;
}

.favorite-card.selection-mode {
	padding-left: 54px;
}

.favorite-card.selected {
	box-shadow: inset 0 0 0 1px #5d8df7;
}

.favorite-select-toggle {
	position: absolute;
	left: 18px;
	top: 50%;
	width: 18px;
	height: 18px;
	border: 1px solid #c4c8d4;
	border-radius: 50%;
	transform: translateY(-50%);
	box-sizing: border-box;
	background: #ffffff;
}

.favorite-select-toggle.checked {
	border-color: #4b7bec;
	background: #4b7bec;
}

.favorite-select-toggle.checked::after {
	content: "";
	position: absolute;
	left: 5px;
	top: 2px;
	width: 5px;
	height: 9px;
	border-right: 2px solid #ffffff;
	border-bottom: 2px solid #ffffff;
	transform: rotate(45deg);
}

.favorite-card-main {
	display: flex;
	align-items: flex-start;
	min-height: 86px;
}

.favorite-card.picker-mode .favorite-card-main {
	justify-content: space-between;
}

.favorite-content {
	flex: 1;
	min-width: 0;
	padding-right: 16px;
}

.favorite-card.picker-mode .favorite-content {
	align-self: flex-start;
	text-align: left;
}

.favorite-card-title {
	font-size: 16px;
	line-height: 24px;
	color: #1f2329;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.favorite-summary,
.favorite-file-size {
	margin-top: 6px;
	font-size: 14px;
	line-height: 20px;
	color: #8a8f99;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.favorite-link-summary {
	color: #2b62c9;
	text-decoration: underline;
	cursor: pointer;
}

.favorite-merged-lines {
	margin-top: 8px;
}

.favorite-merged-line {
	font-size: 14px;
	line-height: 22px;
	color: #8a8f99;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.favorite-image-preview {
	width: 88px;
	height: 88px;
	object-fit: cover;
	border-radius: 6px;
	background: #eeeeee;
	flex: 0 0 auto;
}

.favorite-file-thumb {
	width: 78px;
	height: 88px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
}

.favorite-file-paper {
	position: relative;
	width: 58px;
	height: 72px;
	border-radius: 5px;
	background: #f15245;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.favorite-file-fold {
	position: absolute;
	top: 0;
	right: 0;
	width: 20px;
	height: 20px;
	background: rgba(255, 255, 255, 0.24);
	border-bottom-left-radius: 4px;
}

.favorite-file-ext {
	color: #ffffff;
	font-size: 13px;
	font-weight: 700;
}

.favorite-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 18px;
	font-size: 13px;
	color: #b1b2ba;
}

.favorite-footer.picker-mode {
	display: flex;
	justify-content: space-between;
	gap: 12px;
}

.favorite-source {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.favorite-time {
	min-width: 0;
	font-size: 13px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.favorite-footer.picker-mode .favorite-source {
	flex: 1;
	text-align: left;
}

.favorite-footer.picker-mode .favorite-time {
	flex: 0 0 auto;
	max-width: 220px;
	text-align: right;
}

.favorite-multi-select-bar {
	position: absolute;
	left: 50%;
	bottom: 22px;
	z-index: 9998;
	display: flex;
	align-items: center;
	gap: 8px;
	width: 480px;
	max-width: calc(100vw - 88px);
	padding: 12px 14px;
	border-radius: 8px;
	box-sizing: border-box;
	background: #ffffff;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
	transform: translateX(-50%);
}

.favorite-multi-select-count {
	flex: 1;
	min-width: 0;
	font-size: 14px;
	color: #475569;
}

.merged-forward-detail-mask {
	position: fixed;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 10000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.35);
}

.merged-forward-detail-panel {
	width: 560px;
	max-width: calc(100vw - 48px);
	height: 680px;
	max-height: calc(100vh - 80px);
	background: #f4f5f8;
	border-radius: 12px;
	overflow: hidden;
	box-sizing: border-box;
	box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
}

.merged-forward-detail-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 56px;
	padding: 0 18px;
	background: #ffffff;
	border-bottom: 1px solid #e6e8ee;
	box-sizing: border-box;
}

.merged-forward-detail-title {
	font-size: 18px;
	font-weight: 700;
	color: #1f2933;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.merged-forward-detail-close {
	color: #999999;
	cursor: pointer;
}

.merged-forward-detail-body {
	height: calc(100% - 56px);
	padding: 18px 22px 32px;
	box-sizing: border-box;
	overflow-y: auto;
}

.merged-forward-record {
	padding: 0 0 18px;
	margin-bottom: 18px;
	border-bottom: 1px solid #e6e8ee;
	text-align: left;
}

.merged-forward-record-meta {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 7px;
}

.merged-forward-record-name {
	font-size: 13px;
	line-height: 18px;
	color: #777777;
}

.merged-forward-record-time {
	margin-left: 10px;
	font-size: 12px;
	line-height: 18px;
	color: #a0a4aa;
	white-space: nowrap;
}

.merged-forward-record-bubble {
	display: inline-block;
	max-width: 86%;
	padding: 10px 13px;
	background: #ffffff;
	border-radius: 10px;
	font-size: 15px;
	line-height: 22px;
	color: #202124;
	word-break: break-word;
	white-space: pre-wrap;
}

.merged-forward-record-image {
	display: block;
	max-width: 180px;
	max-height: 180px;
	border-radius: 8px;
	object-fit: cover;
	background: #eef0f3;
}

.merged-forward-record-file,
.merged-forward-record-card {
	display: flex;
	align-items: center;
	width: 280px;
	padding: 12px 14px;
	background: #ffffff;
	border-radius: 4px;
	box-sizing: border-box;
	cursor: pointer;
}

.merged-forward-record-file-info {
	flex: 1;
	min-width: 0;
	margin-right: 12px;
}

.merged-forward-record-file-name {
	font-size: 15px;
	line-height: 22px;
	font-weight: 600;
	color: #202124;
	word-break: break-word;
}

.merged-forward-record-file-size {
	margin-top: 6px;
	font-size: 13px;
	line-height: 18px;
	color: #8a8f99;
}

.merged-forward-record-file-icon {
	position: relative;
	width: 42px;
	height: 50px;
	flex: 0 0 42px;
}

.merged-forward-record-file-icon .file-icon-paper {
	position: absolute;
	right: 0;
	top: 2px;
	width: 34px;
	height: 42px;
	border-radius: 3px;
	background-color: #d42e07;
	box-shadow: 0 2px 6px rgba(15, 23, 42, 0.16);
	overflow: hidden;
}

.merged-forward-record-file-icon .file-icon-fold {
	position: absolute;
	right: 0;
	top: 0;
	width: 12px;
	height: 12px;
	background: rgba(255, 255, 255, 0.82);
	clip-path: polygon(0 0, 100% 0, 100% 100%);
}

.merged-forward-record-file-icon .file-icon-mark {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 6px;
	color: #ffffff;
	font-size: 20px;
	line-height: 22px;
	font-weight: 800;
	text-align: center;
	text-transform: uppercase;
}

.merged-forward-record-file-icon.file-icon-audio .file-icon-paper { background-color: #8b5cf6; }
.merged-forward-record-file-icon.file-icon-video .file-icon-paper { background-color: #ef4444; }
.merged-forward-record-file-icon.file-icon-word .file-icon-paper { background-color: #2b6fdb; }
.merged-forward-record-file-icon.file-icon-excel .file-icon-paper { background-color: #1f9d55; }
.merged-forward-record-file-icon.file-icon-ppt .file-icon-paper { background-color: #f0642f; }
.merged-forward-record-file-icon.file-icon-pdf .file-icon-paper { background-color: #d42e07; }
.merged-forward-record-file-icon.file-icon-image .file-icon-paper { background-color: #0ea5e9; }
.merged-forward-record-file-icon.file-icon-archive .file-icon-paper { background-color: #a16207; }
.merged-forward-record-file-icon.file-icon-code .file-icon-paper { background-color: #475569; }
.merged-forward-record-file-icon.file-icon-default .file-icon-paper { background-color: #d42e07; }

.merged-forward-record-card {
	display: block;
}

.merged-forward-record-card-title {
	font-size: 16px;
	line-height: 22px;
	font-weight: 700;
	color: #202124;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.merged-forward-record-card-lines {
	margin-top: 8px;
}

.merged-forward-record-card-line {
	font-size: 13px;
	line-height: 20px;
	color: #777777;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.merged-forward-record-card-divider {
	height: 1px;
	margin: 10px 0 8px;
	background: #eeeeee;
}

.merged-forward-record-card-tip {
	font-size: 13px;
	line-height: 18px;
	color: #999999;
}
</style>
